import { useState } from 'react';
import { CloudUpload, Download, ExternalLink, FileText, Mail, Sparkles } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { Meeting, MeetingMinutes } from '../../lib/meetingLogApi';
import { downloadMeetingMinutesDocx } from '../../lib/meetingLogApi';
import {
  useEmailMeetingMinutes,
  useGenerateMeetingMinutes,
  useGeneratingMeetingMinutes,
  useMeetingMinutes,
  useSaveMeetingMinutesToDrive,
} from '../../hooks/useMeetingLog';

/**
 * „Minuta întâlnirii” — one button writes the minutes from the transcript
 * (a paid API call, so the card says what each one cost) and the result is
 * downloaded as a Word document on the Firmio template. Every generation is
 * kept; the latest is shown in full, older ones stay listed below.
 */
export interface MeetingMinutesCardProps {
  meeting: Meeting;
  /** Hide the generate button (no canEditNotes). */
  readOnly?: boolean;
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function formatMoney(usd: number, eur: number): string {
  return `$${usd.toFixed(3)} (≈ ${eur.toFixed(3)} €)`;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * „Generează minuta”: the same button wherever it sits (the minutes card, the
 * transcript toolbar). Every instance shares one pending state through the
 * mutation key, so pressing one makes all of them read „Se scrie minuta…”.
 */
export function GenerateMinutesButton({
  meeting,
  variant = 'solid',
  onError,
}: {
  meeting: Meeting;
  variant?: 'solid' | 'outline';
  onError?: (message: string) => void;
}) {
  const { t } = useI18n();
  const { data: minutes = [] } = useMeetingMinutes(meeting.id);
  const generate = useGenerateMeetingMinutes(meeting.id);
  const generating = useGeneratingMeetingMinutes(meeting.id);
  const [localError, setLocalError] = useState<string | null>(null);
  const hasTranscript = meeting.meetingNotes.length > 0;

  const label = generating
    ? t('meetings.minutes.generating')
    : minutes.length
      ? t('meetings.minutes.regenerate')
      : t('meetings.minutes.generate');
  const cls =
    variant === 'solid'
      ? 'inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50'
      : 'inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => {
          setLocalError(null);
          generate.mutate(undefined, {
            onError: (err) => {
              const msg = errorText(err, t('meetings.minutes.failed'));
              if (onError) onError(msg);
              else setLocalError(msg);
            },
          });
        }}
        disabled={generating || !hasTranscript}
        title={!hasTranscript ? t('meetings.minutes.needsTranscript') : undefined}
        className={cls}
      >
        <Sparkles size={15} className={generating ? 'animate-pulse' : undefined} />
        {label}
      </button>
      {localError && <span className="text-xs text-red-600">{localError}</span>}
    </span>
  );
}

export function MeetingMinutesCard({ meeting, readOnly = false }: MeetingMinutesCardProps) {
  const { t } = useI18n();
  // A new meeting's minutes are written at the end of the transcript
  // pipeline, so keep polling while any note is still being processed.
  const processing = meeting.meetingNotes.some(
    (n) => n.ingestStatus === 'PENDING' || n.ingestStatus === 'PROCESSING',
  );
  const { data: minutes = [], isLoading } = useMeetingMinutes(meeting.id, processing);
  const generating = useGeneratingMeetingMinutes(meeting.id);
  const saveToDrive = useSaveMeetingMinutesToDrive(meeting.id);
  const sendEmail = useEmailMeetingMinutes(meeting.id);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSaveToDrive = (m: MeetingMinutes) => {
    setError(null);
    saveToDrive.mutate(m, {
      onError: (err) => setError(errorText(err, t('meetings.minutes.failed'))),
    });
  };
  const onSendEmail = (m: MeetingMinutes) => {
    setError(null);
    sendEmail.mutate(m, {
      onError: (err) => setError(errorText(err, t('meetings.minutes.failed'))),
    });
  };

  const hasTranscript = meeting.meetingNotes.length > 0;
  const latest: MeetingMinutes | undefined = minutes[0];
  const older = minutes.slice(1);

  const onDownload = async (m: MeetingMinutes) => {
    setError(null);
    setDownloading(m.id);
    try {
      await downloadMeetingMinutesDocx(meeting.id, m);
    } catch (err) {
      setError(errorText(err, t('meetings.minutes.failed')));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <FileText size={18} className="text-brand-600" />
            {t('meetings.minutes.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t('meetings.minutes.hint')}</p>
        </div>
        {!readOnly && <GenerateMinutesButton meeting={meeting} onError={setError} />}
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-gray-400">…</p>
        ) : !latest ? (
          <p className="text-sm text-gray-400">
            {generating || processing
              ? t('meetings.minutes.generating')
              : hasTranscript
                ? t('meetings.minutes.empty')
                : t('meetings.minutes.needsTranscript')}
          </p>
        ) : (
          <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{latest.content.title}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {t('meetings.minutes.generatedAt')} {formatWhen(latest.createdAt)} ·{' '}
                  {latest.model} · {t('meetings.minutes.cost')}{' '}
                  {formatMoney(latest.costUsd, latest.costEur)} ·{' '}
                  {latest.inputTokens.toLocaleString('ro-RO')} +{' '}
                  {latest.outputTokens.toLocaleString('ro-RO')} {t('meetings.minutes.tokens')}
                </div>
                {latest.emailedTo.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500">
                    {t('meetings.minutes.emailedTo')} {latest.emailedTo.join(', ')}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!readOnly && !latest.driveUrl && (
                  <button
                    type="button"
                    onClick={() => onSaveToDrive(latest)}
                    disabled={saveToDrive.isPending}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <CloudUpload size={16} />
                    {t('meetings.minutes.saveToDrive')}
                  </button>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onSendEmail(latest)}
                    disabled={sendEmail.isPending}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <Mail size={16} />
                    {sendEmail.isPending
                      ? t('meetings.minutes.sending')
                      : t('meetings.minutes.sendEmail')}
                  </button>
                )}
                {latest.driveUrl && (
                  <a
                    href={latest.driveUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
                  >
                    <ExternalLink size={16} />
                    {t('meetings.minutes.openInDrive')}
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => void onDownload(latest)}
                  disabled={downloading === latest.id}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                >
                  <Download size={16} />
                  {t('meetings.minutes.download')}
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-2 text-sm text-gray-800">
              {latest.content.summary.map((p, i) => (
                <p key={i}>{p.replace(/\*\*/g, '')}</p>
              ))}
            </div>
            {latest.content.actions.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {latest.content.actions.map((a, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-semibold text-brand-600">{i + 1}.</span>
                    <span>
                      {a.action} <span className="text-gray-500">— {a.owner}</span>
                      {a.dueDate && <span className="text-gray-500"> · {a.dueDate}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {older.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t('meetings.minutes.history')}
            </div>
            <ul className="mt-2 divide-y divide-gray-100 text-sm">
              {older.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-gray-600">
                    {formatWhen(m.createdAt)} · {m.model} · {formatMoney(m.costUsd, m.costEur)}
                  </span>
                  <span className="flex items-center gap-3">
                    {m.driveUrl && (
                      <a
                        href={m.driveUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                      >
                        <ExternalLink size={14} />
                        {t('meetings.minutes.openInDrive')}
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => void onDownload(m)}
                      disabled={downloading === m.id}
                      className="inline-flex items-center gap-1 text-brand-700 hover:underline disabled:opacity-50"
                    >
                      <Download size={14} />
                      {t('meetings.minutes.download')}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
